import React, { useState, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { paths } from "utils/constants";
import { onUserLogout } from "store/asyncThunk";
import { toggleRealDemo } from "store/slices/main";
import useMediaQuery from "hooks/useMediaQuery";
import ProtectedContent from "components/UI/ProtectedContent";
import Logo from "components/UI/Logo/Logo";
import ProfileAvatar from "components/UI/ProfileAvatar";

import { Dropdown } from "antd";
import "./ModernHeader.scss";

const userMenuItems = [
  {
    label: "Settings",
    key: "settings",
  },
  {
    label: "Logout",
    key: "0",
  },
];

const ModernHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 700px)");

  const user = useSelector((state) => state.main.user);
  const accountType = useSelector((state) => state.main.accountType);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { name: "Dashboard", path: paths.home },
    {
      name: "Agent",
      path: paths.agent,
      protected: true,
      requiredPermissions: ["manager"],
    },
    {
      name: "Create Model",
      path: paths.createModel,
      protected: true,
      requiredPermissions: ["manager"],
    },
    {
      name: "Options Simulator",
      path: paths.optionSimulator,
      protected: true,
      requiredPermissions: ["manager"],
    },
    { name: "AI Backtests", path: paths.ai },
    { name: "Strategies", path: paths.strategies },
    { name: "Open Positions", path: paths.openPositions },
    { name: "Trades History", path: paths.tradesHistory },
    {
      name: "Admin panel",
      path: paths.admin,
      protected: true,
      requiredPermissions: ["manager"],
    },
  ];

  const handleLogout = () => {
    dispatch(onUserLogout());
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
  };

  const handleToggleRealDemo = () => {
    dispatch(toggleRealDemo());
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleOnClick = (e) => {
    if (e.key === "0") {
      handleLogout();
    } else if (e.key === "settings") {
      navigate(paths.userSettings);
    }
  };

  const renderNavigationItem = (item) => {
    const navButton = (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
        onClick={() => handleNavigation(item.path)}
      >
        <span className="nav-text">{item.name}</span>
      </NavLink>
    );

    const mobileNavButton = (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) =>
          `mobile-nav-item ${isActive ? "active" : ""}`
        }
        onClick={() => handleNavigation(item.path)}
      >
        <span className="mobile-nav-text">{item.name}</span>
      </NavLink>
    );

    // Handle conditional items
    if (item.conditional && !item.condition) {
      return null;
    }

    // Handle protected items
    if (item.protected) {
      return (
        <ProtectedContent
          key={item.path}
          requiredPermissions={item.requiredPermissions}
        >
          {isMobile ? mobileNavButton : navButton}
        </ProtectedContent>
      );
    }

    // Regular items
    return isMobile ? mobileNavButton : navButton;
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore body scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="modern-header">
      <div className="header-container">
        {/* Left Section - Logo and Desktop Navigation */}
        <div className="header-left">
          <div className="lg:block hidden">
            <Logo />
          </div>
          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navigationItems.map(renderNavigationItem)}
          </nav>
        </div>

        {/* Right Section - Controls and User */}
        <div className="header-right">
          {/* Account Type Switcher */}
          <div className="account-switcher">
            <button
              className={`account-btn ${
                accountType === "demo" ? "active" : ""
              }`}
              onClick={() => handleToggleRealDemo()}
            >
              Demo
            </button>
            <button
              className={`account-btn ${
                accountType === "real" ? "active" : ""
              }`}
              onClick={() => handleToggleRealDemo()}
            >
              Live
            </button>
          </div>

          {/* User Profile */}
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleOnClick,
            }}
            trigger={["click"]}
          >
            <div className="user-profile">
              <ProfileAvatar user={user} showTooltip={false} />
              <span className="user-name">Hello, {user?.firstName}</span>
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-content">
          {/* Mobile Navigation */}
          <nav className="mobile-nav">
            {navigationItems.map(renderNavigationItem)}
          </nav>

          {/* Mobile User Actions */}
          <div className="mobile-user-actions">
            <button
              className="mobile-action-btn"
              onClick={() => navigate(paths.userSettings)}
            >
              <Cog6ToothIcon className="w-5 h-5" />
              <span>Settings</span>
            </button>
            <button
              className="mobile-action-btn mobile-action-btn--logout"
              onClick={handleLogout}
            >
              <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu} />
      )}
    </header>
  );
};

export default ModernHeader;
